import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useCompany } from '../context/CompanyContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import './Finance.css';

/* ── Data ─────────────────────────────────────────────── */
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CUR_MONTH = new Date().getMonth();

const REVENUE_DATA = [180000,210000,195000,240000,228000,260000,175000,290000,310000,275000,320000,240000];

const INIT_INVOICES = [
  { id:'INV-001', client:'Kavitha Rao',    company:'Rao Infra',          amount:120000, status:'paid',    date:'2026-05-01', due:'2026-05-15', lead:'Closed' },
  { id:'INV-002', client:'Deepak Singh',   company:'Singh Logistics',    amount:88000,  status:'pending', date:'2026-05-10', due:'2026-05-31', lead:'Hot' },
  { id:'INV-003', client:'Anand Kumar',    company:'Techwave Solutions', amount:45000,  status:'pending', date:'2026-05-15', due:'2026-05-30', lead:'Hot' },
  { id:'INV-004', client:'Rajesh Nair',    company:'Nair Exports',       amount:72000,  status:'pending', date:'2026-05-18', due:'2026-06-05', lead:'Warm' },
  { id:'INV-005', client:'Suresh Menon',   company:'Menon Traders',      amount:35000,  status:'overdue', date:'2026-04-20', due:'2026-05-05', lead:'Contacted' },
  { id:'INV-006', client:'Sneha Corp',     company:'Sneha Corp',         amount:55000,  status:'paid',    date:'2026-04-15', due:'2026-04-30', lead:'Closed' },
  { id:'INV-007', client:'Vikram Nanda',   company:'Nanda Auto',         amount:52000,  status:'draft',   date:'2026-05-20', due:'2026-06-10', lead:'Contacted' },
  { id:'INV-008', client:'Sreelatha K',    company:'Sreelatha Silks',    amount:31000,  status:'pending', date:'2026-05-22', due:'2026-06-07', lead:'Warm' },
];

const EXPENSES = [
  { id:1, category:'Meta Ads',      amount:18000, date:'2026-05-01', note:'May campaign budget' },
  { id:2, category:'Salaries',      amount:95000, date:'2026-05-01', note:'May payroll' },
  { id:3, category:'Office Rent',   amount:22000, date:'2026-05-05', note:'May rent' },
  { id:4, category:'Software',      amount:7499,  date:'2026-05-27', note:'RyzenOS subscription' },
  { id:5, category:'Travel',        amount:4200,  date:'2026-05-12', note:'Client visits' },
  { id:6, category:'Google Ads',    amount:12000, date:'2026-05-08', note:'Search campaign' },
];

const STATUS_TABS = ['All','Paid','Pending','Overdue','Draft'];
const fmtINR  = (v) => v >= 100000 ? '₹'+(v/100000).toFixed(1)+'L' : '₹'+(v/1000).toFixed(0)+'K';
const fmtFull = (v) => '₹'+v.toLocaleString('en-IN');
const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});

/* ── Icons ────────────────────────────────────────────── */
const PlusIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const CloseIcon   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const DownloadIcon= () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const ReceiptIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>;

/* ── Status badge ─────────────────────────────────────── */
function InvBadge({ status }) {
  const map = { paid:'fn-s-green', pending:'fn-s-amber', overdue:'fn-s-red', draft:'fn-s-gray' };
  return <span className={`fn-badge ${map[status]||''}`}>{status.charAt(0).toUpperCase()+status.slice(1)}</span>;
}

/* ── SVG Bar Chart ─────────────────────────────────────── */
function RevenueChart({ data }) {
  const nonZero = data.filter(v => v > 0);
  const max = nonZero.length ? Math.max(...nonZero) : 1;
  const W = 800, H = 110, PL = 8, PR = 8, PT = 8, PB = 20;
  const cW = W - PL - PR;
  const cH = H - PT - PB;
  const bW = Math.floor(cW / data.length) - 4;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="fn-chart-svg" preserveAspectRatio="none">
      {data.map((v, i) => {
        const active = i === CUR_MONTH;
        const bH = v > 0 ? Math.max(4, (v / max) * cH) : 3;
        const x = PL + i * (cW / data.length) + 2;
        const y = PT + cH - bH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={bW} height={bH} rx="3"
              fill={active ? '#F5A623' : 'rgba(245,166,35,0.22)'}
            />
            <text x={x + bW / 2} y={H - 4} textAnchor="middle"
              fontSize="8" fill="currentColor" opacity="0.38">{MONTHS[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Invoice Panel ────────────────────────────────────── */
function InvoicePanel({ inv, theme, onClose, onUpdate, company }) {
  const [status, setStatus] = useState(inv.status);
  return (
    <div className="fn-panel-wrap">
      <div className="fn-overlay" onClick={onClose} />
      <div className="fn-panel" data-theme={theme}>
        <div className="fn-panel-hd">
          <div>
            <div className="fn-panel-id">{inv.id}</div>
            <div className="fn-panel-client">{inv.client}</div>
            <div className="fn-panel-co">{inv.company}</div>
          </div>
          <button className="fn-icon-btn" onClick={onClose}><CloseIcon /></button>
        </div>

        {company.name && (
          <div className="fn-co-header">
            <div className="fn-co-name">{company.name}</div>
            {company.address && <div className="fn-co-addr">{company.address}{company.city ? ', '+company.city : ''}</div>}
            {company.gstin && <div className="fn-co-gst">GSTIN: {company.gstin}</div>}
            {company.phone && <div className="fn-co-addr">{company.phone}</div>}
            <div className="fn-co-divider"/>
          </div>
        )}
        <div className="fn-panel-amount">{fmtFull(inv.amount)}</div>

        <div className="fn-panel-body">
          <div className="fn-kv">
            <span className="fn-k">Invoice date</span><span className="fn-v">{fmtDate(inv.date)}</span>
            <span className="fn-k">Due date</span><span className="fn-v">{fmtDate(inv.due)}</span>
            <span className="fn-k">Lead status</span><span className="fn-v">{inv.lead}</span>
          </div>

          <div className="fn-panel-sec">
            <div className="fn-sec-lbl">Payment status</div>
            <div className="fn-status-btns">
              {['paid','pending','overdue','draft'].map(s => (
                <button key={s} className={`fn-att-btn${status===s?' active':''}`}
                  style={status===s?{borderColor:s==='paid'?'#22C55E':s==='overdue'?'#EF4444':s==='pending'?'#F5A623':'#666'}:{}}
                  onClick={()=>setStatus(s)}>
                  {s.charAt(0).toUpperCase()+s.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="fn-panel-ft">
          <button className="fn-btn-sec" style={{flex:1}}><DownloadIcon /> Download PDF</button>
          <button className="fn-btn-pri" style={{flex:1}} onClick={()=>{onUpdate({...inv,status});onClose();}}>Save</button>
        </div>
      </div>
    </div>
  );
}

/* ── Add Invoice Modal ────────────────────────────────── */
function AddModal({ theme, onClose, onAdd }) {
  const [form, setForm] = useState({ client:'', company:'', amount:'', date:new Date().toISOString().split('T')[0], due:'', status:'draft' });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const submit = () => {
    if (!form.client.trim() || !form.amount) return;
    const num = parseInt(INIT_INVOICES.length) + 1;
    onAdd({ ...form, id:`INV-00${num+8}`, amount:parseInt(form.amount), lead:'New' });
    onClose();
  };
  return (
    <div className="fn-modal-overlay" onClick={onClose}>
      <div className="fn-modal" data-theme={theme} onClick={e=>e.stopPropagation()}>
        <div className="fn-modal-hd">
          <span className="fn-modal-title">New Invoice</span>
          <button className="fn-icon-btn" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="fn-modal-body">
          <div className="fn-form-row">
            <div className="fn-form-grp"><label className="fn-lbl">Client name *</label><input className="fn-inp" placeholder="Full name" value={form.client} onChange={e=>set('client',e.target.value)}/></div>
            <div className="fn-form-grp"><label className="fn-lbl">Company</label><input className="fn-inp" placeholder="Company name" value={form.company} onChange={e=>set('company',e.target.value)}/></div>
          </div>
          <div className="fn-form-row">
            <div className="fn-form-grp"><label className="fn-lbl">Amount (₹) *</label><input className="fn-inp" type="number" placeholder="e.g. 50000" value={form.amount} onChange={e=>set('amount',e.target.value)}/></div>
            <div className="fn-form-grp"><label className="fn-lbl">Status</label>
              <select className="fn-inp fn-sel" value={form.status} onChange={e=>set('status',e.target.value)}>
                {['draft','pending','paid','overdue'].map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="fn-form-row">
            <div className="fn-form-grp"><label className="fn-lbl">Invoice date</label><input className="fn-inp" type="date" value={form.date} onChange={e=>set('date',e.target.value)}/></div>
            <div className="fn-form-grp"><label className="fn-lbl">Due date</label><input className="fn-inp" type="date" value={form.due} onChange={e=>set('due',e.target.value)}/></div>
          </div>
        </div>
        <div className="fn-modal-ft">
          <button className="fn-btn-sec" onClick={onClose}>Cancel</button>
          <button className="fn-btn-pri" onClick={submit}>Create Invoice</button>
        </div>
      </div>
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────── */
export default function Finance() {
  const { theme } = useTheme();
  const { company } = useCompany();
  const [invoices, setInvoices] = useState(INIT_INVOICES);
  const [tab, setTab] = useState('All');
  const [view, setView] = useState('invoices'); // 'invoices' | 'expenses'
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const filtered = tab === 'All' ? invoices : invoices.filter(i => i.status === tab.toLowerCase());

  const totalRevenue   = invoices.filter(i=>i.status==='paid').reduce((s,i)=>s+i.amount,0);
  const pending        = invoices.filter(i=>i.status==='pending').reduce((s,i)=>s+i.amount,0);
  const overdue        = invoices.filter(i=>i.status==='overdue').reduce((s,i)=>s+i.amount,0);
  const totalExpenses  = EXPENSES.reduce((s,e)=>s+e.amount,0);
  const netProfit      = totalRevenue - totalExpenses;

  const handleUpdate = (updated) => setInvoices(inv => inv.map(i => i.id===updated.id ? updated : i));
  const handleAdd    = (inv) => setInvoices(i => [inv, ...i]);

  const TopActions = (
    <div className="fn-top-acts">
      <div className="fn-view-tabs">
        <button className={`fn-view-tab${view==='invoices'?' active':''}`} onClick={()=>setView('invoices')}><ReceiptIcon /> Invoices</button>
        <button className={`fn-view-tab${view==='expenses'?' active':''}`} onClick={()=>setView('expenses')}>Expenses</button>
      </div>
      {view==='invoices' && <button className="fn-add-btn" onClick={()=>setShowAdd(true)}><PlusIcon /> New Invoice</button>}
    </div>
  );

  return (
    <div className="fn-wrap" data-theme={theme}>
      <Sidebar />
      <div className="fn-main">
        <Topbar title="Finance" subtitle={`Net profit this month: ${fmtINR(netProfit)}`} actions={TopActions} />

        {/* Summary cards */}
        <div className="fn-summary">
          {[
            { label:'Revenue collected', val:fmtINR(totalRevenue), sub:`${invoices.filter(i=>i.status==='paid').length} invoices paid`, color:'#22C55E' },
            { label:'Pending',           val:fmtINR(pending),      sub:`${invoices.filter(i=>i.status==='pending').length} invoices`, color:'#F5A623' },
            { label:'Overdue',           val:fmtINR(overdue),      sub:`${invoices.filter(i=>i.status==='overdue').length} invoices`, color:'#EF4444' },
            { label:'Total expenses',    val:fmtINR(totalExpenses),sub:'This month', color:'#3B82F6' },
          ].map(s=>(
            <div key={s.label} className="fn-sum-card">
              <div className="fn-sum-lbl">{s.label}</div>
              <div className="fn-sum-val" style={{color:s.color}}>{s.val}</div>
              <div className="fn-sum-sub">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Revenue chart */}
        <div className="fn-chart-wrap">
          <div className="fn-chart-hd">
            <span className="fn-chart-title">Revenue — 2026</span>
            <span className="fn-chart-cur">{MONTHS[CUR_MONTH]}: {fmtINR(REVENUE_DATA[CUR_MONTH])}</span>
          </div>
          <RevenueChart data={REVENUE_DATA} />
        </div>

        {/* Invoices / Expenses */}
        {view === 'invoices' && (
          <div className="fn-table-wrap">
            <div className="fn-status-tabs">
              {STATUS_TABS.map(t=>(
                <button key={t} className={`fn-stab${tab===t?' active':''}`} onClick={()=>setTab(t)}>{t}</button>
              ))}
            </div>
            <table className="fn-table">
              <thead>
                <tr>{['Invoice','Client','Amount','Status','Date','Due'].map(h=><th key={h} className="fn-th">{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map(inv=>(
                  <tr key={inv.id} className="fn-tr" onClick={()=>setSelected(inv)}>
                    <td className="fn-td fn-td-id">{inv.id}</td>
                    <td className="fn-td">
                      <div className="fn-tr-name">{inv.client}</div>
                      <div className="fn-tr-co">{inv.company}</div>
                    </td>
                    <td className="fn-td fn-td-amount">{fmtFull(inv.amount)}</td>
                    <td className="fn-td"><InvBadge status={inv.status} /></td>
                    <td className="fn-td fn-td-muted">{fmtDate(inv.date)}</td>
                    <td className={`fn-td${inv.status==='overdue'?' fn-td-over':' fn-td-muted'}`}>{fmtDate(inv.due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {view === 'expenses' && (
          <div className="fn-table-wrap">
            <table className="fn-table">
              <thead>
                <tr>{['Category','Amount','Date','Note'].map(h=><th key={h} className="fn-th">{h}</th>)}</tr>
              </thead>
              <tbody>
                {EXPENSES.map(exp=>(
                  <tr key={exp.id} className="fn-tr fn-tr-static">
                    <td className="fn-td"><span className="fn-cat-pill">{exp.category}</span></td>
                    <td className="fn-td fn-td-amount">{fmtFull(exp.amount)}</td>
                    <td className="fn-td fn-td-muted">{fmtDate(exp.date)}</td>
                    <td className="fn-td fn-td-muted">{exp.note}</td>
                  </tr>
                ))}
                <tr className="fn-tr-total">
                  <td className="fn-td" colSpan={1}><strong>Total</strong></td>
                  <td className="fn-td fn-td-amount"><strong>{fmtFull(totalExpenses)}</strong></td>
                  <td className="fn-td" colSpan={2}/>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <InvoicePanel inv={selected} theme={theme} onClose={()=>setSelected(null)} onUpdate={handleUpdate} company={company}/>}
      {showAdd   && <AddModal theme={theme} onClose={()=>setShowAdd(false)} onAdd={handleAdd}/>}
    </div>
  );
}
